import { showOperationError } from "../lib/notifications";
import { toast } from "sonner";
import { EscalationSettings, escalationStageError } from "@ai-crm/agent-ui";
import {
  emptyFunnelPromptFields,
  editablePromptFields,
  type FunnelPromptFields,
} from "@ai-crm/shared";
import { AdminPromptEditor } from "../components/funnel-prompt-editor";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import type { FormEvent } from "react";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import { PageHeader } from "../components/layout";
import type {
  AdminFunnelSettings,
  AdminWazzupChannel,
  LlmModel,
} from "../lib/api";
import { adminApi } from "../lib/api";
import { useAdminAuth } from "../lib/auth";

const models: Array<{ value: LlmModel; label: string }> = [
  { value: "GPT4O_MINI", label: "GPT-4o mini" },
  { value: "GPT4O", label: "GPT-4o" },
  { value: "CLAUDE_HAIKU", label: "Claude Haiku" },
  { value: "CLAUDE_SONNET", label: "Claude Sonnet" },
];

function isLlmModel(value: string): value is LlmModel {
  return models.some((model) => model.value === value);
}

function errorMessage(error: unknown): string {
  return error instanceof Error
    ? error.message
    : "Не удалось выполнить действие";
}

interface PartialCreation {
  funnel: AdminFunnelSettings;
  channelName: string;
  reason: string;
}

export function FunnelCreatePage() {
  const { id: accountId } = useParams();
  const { token } = useAdminAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [pipelineId, setPipelineId] = useState("");
  const [statusIds, setStatusIds] = useState<string[]>([]);
  const [name, setName] = useState("");
  const [systemPrompt, setSystemPrompt] = useState("");
  const [promptFields, setPromptFields] = useState<FunnelPromptFields>(
    emptyFunnelPromptFields,
  );
  const [model, setModel] = useState<LlmModel>("GPT4O_MINI");
  const [stopPhrase, setStopPhrase] = useState("");
  const [escalationEnabled, setEscalationEnabled] = useState(false);
  const [escalationStatusId, setEscalationStatusId] = useState<string | null>(
    null,
  );
  const [escalate] = useState(false);
  const [active, setActive] = useState(true);
  const [selectedChannelIds, setSelectedChannelIds] = useState<string[]>([]);
  const [pending, setPending] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [partialCreation, setPartialCreation] =
    useState<PartialCreation | null>(null);

  const integration = useQuery({
    queryKey: ["admin", "account", accountId, "integrations"],
    queryFn: () => adminApi.integrationStatus(token!, accountId!),
    enabled: Boolean(token && accountId),
  });
  const pipelines = useQuery({
    queryKey: ["admin", "account", accountId, "amo", "pipelines"],
    queryFn: () => adminApi.amoPipelines(token!, accountId!),
    enabled: Boolean(token && accountId),
  });
  const statuses = useQuery({
    queryKey: [
      "admin",
      "account",
      accountId,
      "amo",
      "pipelines",
      pipelineId,
      "statuses",
    ],
    queryFn: () => adminApi.amoPipelineStatuses(token!, accountId!, pipelineId),
    enabled: Boolean(token && accountId && pipelineId),
  });
  const channels = useQuery({
    queryKey: ["admin", "account", accountId, "wazzup", "channels"],
    queryFn: () => adminApi.wazzupChannels(token!, accountId!),
    enabled: Boolean(token && accountId),
  });

  if (!accountId) return <Navigate to="/accounts" replace />;

  function toggleStatus(statusId: string) {
    setStatusIds((current) =>
      current.includes(statusId)
        ? current.filter((id) => id !== statusId)
        : [...current, statusId],
    );
  }

  function toggleChannel(channelId: string) {
    setSelectedChannelIds((current) =>
      current.includes(channelId)
        ? current.filter((id) => id !== channelId)
        : [...current, channelId],
    );
  }

  async function invalidateAccountData() {
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: ["admin", "account", accountId],
      }),
      queryClient.invalidateQueries({ queryKey: ["admin", "accounts"] }),
      queryClient.invalidateQueries({
        queryKey: ["admin", "account", accountId, "funnels"],
      }),
      queryClient.invalidateQueries({
        queryKey: ["admin", "account", accountId, "amo", "pipelines"],
      }),
      queryClient.invalidateQueries({
        queryKey: ["admin", "account", accountId, "wazzup", "channels"],
      }),
      queryClient.invalidateQueries({
        queryKey: ["admin", "account", accountId, "audit"],
      }),
    ]);
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const stageError = escalationStageError(
      escalationEnabled,
      escalationStatusId,
      statusIds,
    );
    if (stageError) {
      setSubmitError(stageError);
      return;
    }
    if (pending || partialCreation) return;
    if (!pipelineId) {
      setSubmitError("Выберите свободную воронку amoCRM");
      return;
    }
    if (!statuses.isSuccess) {
      setSubmitError("Дождитесь успешной загрузки этапов выбранной воронки");
      return;
    }
    if (!name.trim()) {
      setSubmitError("Укажите название агента");
      return;
    }
    if (!systemPrompt.trim()) {
      setSubmitError("Укажите системный промпт");
      return;
    }

    const currentChannels =
      queryClient.getQueryData<AdminWazzupChannel[]>([
        "admin",
        "account",
        accountId,
        "wazzup",
        "channels",
      ]) ?? channels.data;
    const selectedChannels: AdminWazzupChannel[] = [];
    for (const channelId of selectedChannelIds) {
      const channel = currentChannels?.find((item) => item.id === channelId);
      if (!channel) {
        setSubmitError(
          `Канал Wazzup ${channelId} больше недоступен. Обновите список каналов.`,
        );
        return;
      }
      selectedChannels.push(channel);
    }

    setPending(true);
    setSubmitError(null);
    try {
      const created = await adminApi.createFunnel(token!, accountId!, {
        amoPipelineId: pipelineId,
        amoStatusIds: statusIds,
        name: name.trim(),
        systemPrompt,
        ...editablePromptFields(promptFields),
        model,
        stopPhrase: stopPhrase.trim() || null,
        escalate,
        escalationEnabled,
        escalationStatusId,
        active,
      });

      for (const channel of selectedChannels) {
        try {
          await adminApi.bindChannel(token!, created.id, {
            wazzupChannelId: channel.id,
          });
        } catch (error) {
          setPartialCreation({
            funnel: created,
            channelName: channel.name,
            reason: errorMessage(error),
          });
          await invalidateAccountData();
          return;
        }
      }

      await invalidateAccountData();
      toast.success("Агент создан");
      navigate(`/funnels/${created.id}`);
    } catch (error) {
      showOperationError(error, errorMessage(error), setSubmitError);
    } finally {
      setPending(false);
    }
  }

  const loadErrors = [
    integration.error,
    pipelines.error,
    channels.error,
  ].filter((error): error is Error => error instanceof Error);
  const formDisabled = pending || partialCreation !== null;

  return (
    <div>
      <PageHeader
        title="Новый агент"
        description="Создайте агента в свободной воронке и подключите каналы Wazzup."
        backTo={`/accounts/${accountId}/agents`}
        backLabel="К агентам клиента"
      />

      <div className="mb-4 space-y-2">
        {integration.data && !integration.data.amoConnected ? (
          <p className="panel border-warning/40 p-3 text-sm text-foreground">
            amoCRM не подключена к аккаунту.
          </p>
        ) : null}
        {integration.data && !integration.data.wazzupConnected ? (
          <p className="panel border-warning/40 p-3 text-sm text-foreground">
            Wazzup не подключён к аккаунту.
          </p>
        ) : null}
        {loadErrors.map((error, index) => (
          <p
            key={`${error.message}-${index}`}
            className="panel border-danger/30 p-3 text-sm text-danger"
            role="alert"
          >
            {error.message}
          </p>
        ))}
        {partialCreation ? (
          <div className="panel border-warning/40 p-4" role="alert">
            <p className="font-medium">
              Агент создан, но канал «{partialCreation.channelName}» не
              подключён.
            </p>
            <p className="mt-1 text-sm text-muted-foreground">
              {partialCreation.reason}
            </p>
            <p className="mt-2 text-sm">
              Остальные данные сохранены. Откройте агента и повторите
              подключение вручную.
            </p>
            <Link
              className="mt-3 inline-flex h-10 items-center rounded-md bg-primary px-4 text-sm font-medium text-primary-foreground"
              to={`/funnels/${partialCreation.funnel.id}`}
            >
              Открыть созданного агента
            </Link>
          </div>
        ) : null}
      </div>

      <form className="space-y-4" noValidate onSubmit={handleSubmit}>
        <section className="panel p-4">
          <h2 className="text-lg font-semibold">1. Воронка и этапы amoCRM</h2>
          <label className="mt-4 block text-sm font-medium">
            <span className="mb-1.5 block">Воронка amoCRM</span>
            <select
              className="control"
              required
              value={pipelineId}
              disabled={formDisabled || pipelines.isLoading}
              onChange={(event) => {
                setPipelineId(event.target.value);
                setStatusIds([]);
              }}
            >
              <option value="">Выберите воронку</option>
              {(pipelines.data ?? []).map((pipeline) => (
                <option
                  key={pipeline.id}
                  value={pipeline.id}
                  disabled={pipeline.bound}
                >
                  {pipeline.name}
                </option>
              ))}
            </select>
          </label>

          <fieldset
            className="mt-4"
            disabled={formDisabled || statuses.isLoading || !pipelineId}
          >
            <legend className="text-sm font-medium">Разрешённые этапы</legend>
            <p className="mt-1 text-xs text-muted-foreground">
              Если ничего не выбрать, агент сможет отвечать на всех этапах
              воронки.
            </p>
            <div className="mt-3 grid gap-2 sm:grid-cols-2">
              {(statuses.data ?? []).map((status) => (
                <label
                  key={status.id}
                  className="flex items-center gap-2 rounded-md border p-3 text-sm"
                >
                  <input
                    type="checkbox"
                    checked={statusIds.includes(status.id)}
                    onChange={() => toggleStatus(status.id)}
                  />
                  <span>{status.name}</span>
                </label>
              ))}
            </div>
            {pipelineId && statuses.isLoading ? (
              <p className="mt-3 text-sm text-muted-foreground">
                Загружаем этапы...
              </p>
            ) : null}
            {pipelineId && statuses.error ? (
              <div className="mt-3 text-sm text-danger" role="alert">
                <p>{statuses.error.message}</p>
                <button
                  className="mt-2 inline-flex h-9 items-center justify-center rounded-md border border-danger/30 px-3 font-medium disabled:opacity-50"
                  type="button"
                  disabled={formDisabled}
                  onClick={() => void statuses.refetch()}
                >
                  Повторить загрузку этапов
                </button>
              </div>
            ) : null}
          </fieldset>
        </section>

        <section className="panel p-4">
          <h2 className="text-lg font-semibold">
            2. Промпт, модель и поведение
          </h2>
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            <label className="text-sm font-medium">
              <span className="mb-1.5 block">Название агента</span>
              <input
                className="control"
                required
                value={name}
                disabled={formDisabled}
                onChange={(event) => setName(event.target.value)}
              />
            </label>
            <label className="text-sm font-medium">
              <span className="mb-1.5 block">Модель</span>
              <select
                className="control"
                value={model}
                disabled={formDisabled}
                onChange={(event) => {
                  if (isLlmModel(event.target.value))
                    setModel(event.target.value);
                }}
              >
                {models.map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </select>
            </label>
            <div className="sm:col-span-2">
              <AdminPromptEditor
                token={token!}
                accountId={accountId!}
                value={systemPrompt}
                onChange={setSystemPrompt}
                fields={promptFields}
                onFieldsChange={setPromptFields}
                disabled={formDisabled}
              />
            </div>
            <label className="text-sm font-medium sm:col-span-2">
              <span className="mb-1.5 block">Стоп-фраза</span>
              <input
                className="control"
                value={stopPhrase}
                disabled={formDisabled}
                placeholder="Необязательно"
                onChange={(event) => setStopPhrase(event.target.value)}
              />
            </label>
            <EscalationSettings
              enabled={escalationEnabled}
              target={escalationStatusId}
              working={statusIds}
              stages={statuses.data ?? []}
              onEnabled={setEscalationEnabled}
              onTarget={setEscalationStatusId}
              disabled={pending}
            />
            <label className="flex items-center gap-2 text-sm font-medium">
              <input
                type="checkbox"
                checked={active}
                disabled={formDisabled}
                onChange={(event) => setActive(event.target.checked)}
              />
              Агент активен
            </label>
          </div>
        </section>

        <section className="panel p-4">
          <h2 className="text-lg font-semibold">3. Каналы Wazzup</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Один физический канал можно подключить к нескольким агентам.
          </p>
          <div className="mt-4 grid gap-2 sm:grid-cols-2">
            {(channels.data ?? []).map((channel: AdminWazzupChannel) => (
              <label
                key={channel.id}
                className="flex items-center gap-2 rounded-md border p-3 text-sm"
              >
                <input
                  type="checkbox"
                  aria-label={channel.name}
                  checked={selectedChannelIds.includes(channel.id)}
                  disabled={formDisabled}
                  onChange={() => toggleChannel(channel.id)}
                />
                <span>
                  {channel.name}{" "}
                  <span
                    className="text-xs text-muted-foreground"
                    aria-hidden="true"
                  >
                    ({channel.type})
                  </span>
                </span>
              </label>
            ))}
          </div>
          {channels.data?.length === 0 ? (
            <p className="mt-3 text-sm text-muted-foreground">
              Доступных каналов нет.
            </p>
          ) : null}
        </section>

        <div className="flex flex-col items-start gap-3 sm:flex-row sm:items-center sm:justify-end">
          {submitError ? (
            <p className="text-sm text-danger sm:mr-auto" role="alert">
              {submitError}
            </p>
          ) : null}
          <button
            className="inline-flex h-10 items-center justify-center rounded-md bg-primary px-4 text-sm font-medium text-primary-foreground disabled:cursor-not-allowed disabled:opacity-50"
            type="submit"
            aria-busy={pending}
            disabled={formDisabled || !pipelineId || !statuses.isSuccess}
          >
            {pending ? "Создаём..." : "Создать агента"}
          </button>
        </div>
      </form>
    </div>
  );
}
